'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { AppError, AppErrorCode } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { cn } from '@repo/ui/lib/utils';
import { PasswordInput } from '@repo/ui/password-input';
import { Textarea } from '@repo/ui/textarea';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { useAnalytics } from '@/hooks/use-analytics';
import { authClient } from '@/providers/session';
import { ZPasswordSchema } from '@/types/auth';

const initials = {
  firstName: '',
  lastName: '',
  middleName: '',
  phone: '',
  email: '',
  password: '',
  organizationName: '',
  organizationCountry: '',
  organizationDescription: '',
};

export const ZRegisterOrganizationFormSchema = z
  .object({
    firstName: z.string().min(1, {
      get message() {
        return t('auth.firstNameIsRequired');
      },
    }),
    middleName: z.string().optional(),
    lastName: z.string().min(1, {
      get message() {
        return t('auth.lastNameIsRequired');
      },
    }),
    email: z.email().min(1),
    password: ZPasswordSchema,
    phone: z.string().optional(),
    organizationName: z.string().min(1, {
      get message() {
        return t('auth.organizationNameIsRequired');
      },
    }),
    organizationCountry: z.string().optional(),
    organizationDescription: z.string().optional(),
  })
  .refine(
    (data) => {
      const { email, password } = data;
      const nameFromEmail = email?.split('@')?.[0];
      return nameFromEmail && !password.includes(nameFromEmail);
    },
    {
      get message() {
        return t('auth.passwordShouldNotBeCommonOrBasedOnPersonal');
      },
      path: ['password'],
    },
  );

export const SIGNUP_ERROR_MESSAGES: Record<string, string> = {
  get SIGNUP_DISABLED() {
    return t('extra.signupIsCurrentlyDisabledOrNotAvailableForYourEmail');
  },
  get [AppErrorCode.ALREADY_EXISTS]() {
    return t('extra.userWithThisEmailAlreadyExistsPleaseUseADifferent');
  },
  get [AppErrorCode.INVALID_REQUEST]() {
    return t('extra.weWereUnableToCreateYourAccountPleaseReviewThe');
  },
};
export type TRegisterOrganizationFormSchema = z.infer<typeof ZRegisterOrganizationFormSchema>;

export const RegisterOrganizationForm = () => {
  useLocale();

  const analytics = useAnalytics();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const utmSrc = searchParams.get('utm_source') ?? null;

  const form = useForm<TRegisterOrganizationFormSchema>({
    values: initials,
    mode: 'onBlur',
    resolver: zodResolver(ZRegisterOrganizationFormSchema, { error: validationError }),
  });

  const isSubmitting = form.formState.isSubmitting;

  const onFormSubmit = async (registerData: TRegisterOrganizationFormSchema) => {
    try {
      await authClient.emailPassword.registerOrganization({
        ...registerData,
      });

      await navigate(`/auth/unverified-account`);
      toast.success(t('auth.registrationSuccessful'), {
        get description() {
          return t('auth.youHaveSuccessfullyRegisteredPleaseVerifyYourAccountBy');
        },
        duration: 5000,
      });

      analytics.capture('App: User Sign Up', {
        email: registerData.email,
        timestamp: new Date().toISOString(),
        custom_campaign_params: { src: utmSrc },
      });
    } catch (err) {
      const error = AppError.parseError(err);

      const errorMessage =
        SIGNUP_ERROR_MESSAGES[error.code] ?? SIGNUP_ERROR_MESSAGES.INVALID_REQUEST;

      toast.error(t('auth.anErrorOccurred'), {
        description: errorMessage,
      });
    }
  };

  // const onRegisterOrganizationWithGoogleClick = async () => {
  //   try {
  //     // await authClient.google.signIn();
  //   } catch (err) {
  //     toast.error('An unknown error occurred', {
  //       description:
  //         'We encountered an unknown error while attempting to sign you Up. Please try again later.',
  //     });
  //   }
  // };

  // const onRegisterOrganizationWithOIDCClick = async () => {
  //   try {
  //     // await authClient.oidc.signIn();
  //   } catch (err) {
  //     toast.error('An unknown error occurred', {
  //       description:
  //         'We encountered an unknown error while attempting to sign you Up. Please try again later.',
  //     });
  //   }
  // };

  useEffect(() => {
    const hash = window.location.hash.slice(1);

    const params = new URLSearchParams(hash);

    const email = params.get('email');

    if (email) {
      form.setValue('email', email);
    }
  }, [form]);

  return (
    <>
      <Form {...form}>
        <form
          className="flex w-full flex-1 flex-col gap-y-4"
          onSubmit={form.handleSubmit(onFormSubmit)}
        >
          <div className="space-y-3.5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start sm:gap-4">
              <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium text-foreground text-sm">
                      {t('auth.organizationName')}{' '}
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-9 text-sm sm:h-10"
                        placeholder={t('auth.acmeLtd')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organizationCountry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium text-foreground text-sm">
                      {t('auth.country')}{' '}
                      <span className="font-normal text-muted-foreground">
                        {t('auth.optional')}
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-9 text-sm sm:h-10"
                        placeholder={t('auth.eGUnitedKingdom')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="organizationDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-foreground text-sm">
                    {t('auth.description')}{' '}
                    <span className="font-normal text-muted-foreground">{t('auth.optional')}</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[68px] resize-y text-sm leading-relaxed sm:min-h-[76px]"
                      placeholder={t('auth.oneOrTwoLinesAboutWhatYouDo')}
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-foreground text-sm">
                    {t('reports.firstName2')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-9 text-sm sm:h-10"
                      placeholder={t('reports.firstName2')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-medium text-foreground text-sm">
                    {t('reports.lastName2')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-9 text-sm sm:h-10"
                      placeholder={t('reports.lastName2')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="middleName"
              render={({ field }) => (
                <FormItem className="max-w-full sm:max-w-[min(100%,14rem)]">
                  <FormLabel className="font-medium text-foreground text-sm">
                    {t('auth.middleName')}{' '}
                    <span className="font-normal text-muted-foreground">{t('auth.optional')}</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-9 text-sm sm:h-10"
                      placeholder={t('auth.middleName')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <fieldset className={cn('flex w-full flex-col gap-y-4')} disabled={isSubmitting}>
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem className="sm:col-span-1">
                  <FormLabel className="font-medium text-foreground text-sm">
                    {t('reports.phone')}{' '}
                    <span className="font-normal text-muted-foreground">{t('auth.optional')}</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="h-9 text-sm sm:h-10"
                      placeholder={t('auth.phoneNumber')}
                      type="tel"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.emailAddress2')}</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.password')}</FormLabel>

                  <FormControl>
                    <PasswordInput {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <p className="mt-4 text-muted-foreground text-sm">
              {t('auth.alreadyHaveAnAccount')}{' '}
              <Link className="text-primary duration-200 hover:opacity-70" to="/auth/signin">
                {t('auth.signInInstead')}{' '}
              </Link>
            </p>
          </fieldset>

          <div className="flex items-center">
            <Button
              className="mt-6 w-full"
              loading={form.formState.isSubmitting}
              size="lg"
              type="submit"
            >
              {t('auth.createAccount')}{' '}
            </Button>
          </div>
        </form>
      </Form>
      <p className="mt-6 text-muted-foreground text-xs">
        {t('auth.byProceedingYouAgreeToOur')}{' '}
        <Link className="text-primary duration-200 hover:opacity-70" target="_blank" to="/">
          {t('auth.termsOfService')}{' '}
        </Link>{' '}
        {t('auth.and')}{' '}
        <Link className="text-primary duration-200 hover:opacity-70" target="_blank" to="/">
          {t('auth.privacyPolicy')}{' '}
        </Link>
        .
      </p>
    </>
  );
};
